import clsx from "clsx";
import { Fragment, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ErrorText } from "components/atoms/ErrorText";
import { Link } from "react-router-dom";
import OfflineChat from "pages/support/OfflineChat";

const Hero = () => {
    const [loading, setLoading] = useState(false);

    const chatOfflineRefs = useRef(null);
    const openOfflineChatRefs = useRef(null);

    const initialValues = { email: "", message: "" };

    const disabledFn = () => {
        if (email && message) return false;
        return true;
    };

    const schemaValidations = Yup.object({
        email: Yup.string()
            .email("Invalid Email Address")
            .required("Field Email Required"),
        message: Yup.string()
            .min(20, "Minimum 20 character length")
            .required("Field Message Required")
    });

    const SendMessageHandler = async (values) => {
        setLoading(true);
        try {
            const message = "Thank you! Your submission has been received!";
            setTimeout(() => {
                toast.success(message);
                console.log(values);

                setLoading(false);
            }, 5000);
        } catch (error) {
            console.log(error);
            setLoading(false);

            toast.error("Send Message Failed");
        }
    };

    return (
        <Fragment>
            <OfflineChat childRef={chatOfflineRefs} offlineRefs={openOfflineChatRefs} />
            <header className={clsx(
                "w-full max-w-[1440px] mx-auto relative top-8",
                "flex flex-col tablet:flex-row desktop:flex-row justify-between",
                "px-6 tablet:px-8 desktop:px-[120px]",
                "tablet:pb-16 phone:pb-[60px] pb-[120px] bg-neutral-10"
            )}>
                <div className="w-full tablet:max-w-[384px] tablet:mr-[125px] desktop:max-w-[481px] pt-10 tablet:pt-[81px] desktop:pt-[120px]">
                    <h1 className="text-heading-1 phone:text-[40px] phone:leading-[54px] tablet:text-heading-2 tablet:leading-[64.8px] desktop:text-display font-semibold text-basier-circle-semibold text-neutral-100">Talks to <span className="text-primary-main">Us!</span></h1>
                    <p className="text-neutral-50 font-normal tablet:text-[16px] desktop:text-[16px] text-[16px] tracking-text leading-[27.2px] pt-4 max-w-[468px] tablet:max-w-[352px]">
                        We offer various layers of support. Please pick one that is more appropriate to your needs. We recommend starting with the knowledgebase as we regularly push articles covering various aspects.
                    </p>
                    <div className="border border-neutral-30 w-full tablet:w-[352px] desktop:w-[481px] phone:my-8 my-12 tablet:my-10"></div>

                    <div className="hidden md:block">
                        <h1 className='text-heading-5 tablet:text-lg leading-[27.2px] text-neutral-100 font-semibold'>Support Channels</h1>
                        <div className="mt-6">
                            <div className="flex items-center space-x-4">
                                <img src="/icons/support/livechat.svg" width={24} height={24} alt="live chat" />
                                <p onClick={() => openOfflineChatRefs.current.openModal()} className="text-md hover:cursor-pointer font-medium text-neutral-40 leading-[27.2px]">Live Chat (Offline)</p>
                            </div>
                            <Link to="/support/knowledge-base">
                                <div className="flex items-center space-x-4 mt-4">
                                    <img src="/icons/support/clipboard.svg" width={24} height={24} alt="knowlegde base" />
                                    <p className="text-md font-medium text-neutral-40 leading-[27.2px]">Knowledge Base</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="w-full tablet:max-w-[584px] desktop:max-w-[584px] tablet:pt-[107px] pt-[168px] phone:pt-0 phone:pl-0 pl-[14px]">
                    <Formik initialValues={initialValues} validationSchema={schemaValidations} onSubmit={SendMessageHandler}>
                        {({ errors, touched }) => (
                            <Form>
                                <h3 className="font-normal text-md text-neutral-50 mb-4 tracking-text leading-[16px]">Email Address</h3>
                                <Field type="text" name="email" className={clsx(
                                    "w-full max-w-[584px] h-[56px] px-4 bg-neutral-20 rounded-lg",
                                    errors.email && touched.email ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                )} />
                                <ErrorMessage name="email" component={ErrorText} />
                                <h3 className="font-normal text-md text-neutral-50 mt-[28px] mb-4 tracking-text leading-[16px]">Message</h3>
                                <Field type="text" name="message" id="message" rows="100" cols="50" className={clsx(
                                    "w-full phone:h-[150px] h-[220px] max-w-[584px] p-4 bg-neutral-20 rounded-lg resize-none",
                                    errors.message && touched.message ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                )} />
                                <ErrorMessage name="message" component={ErrorText} />
                                <button disabled={disabledFn} className="w-full tablet:w-[222px] desktop:w-[276px] hover:bg-primary-hover tablet:mt-10 phone:mt-[32px] mt-[28px] py-[18px] text-neutral-10 font-semibold text-md rounded-lg bg-primary-main leading-[19.83px]">{loading ? "Sending..." : "Send Message"}</button>
                            </Form>
                        )}
                    </Formik>
                    <div className="md:hidden phone:block phone:mt-10">
                        <h1 className='text-heading-5 phone:text-lg tablet:text-lg leading-[27.2px] text-neutral-100 font-semibold'>Support Channels</h1>
                        <div className="mt-6">
                            <div className="flex items-center space-x-4">
                                <img src="/icons/support/livechat.svg" className="phone:w-5 phone:h-5" width={24} height={24} alt="live chat" />
                                <p onClick={() => openOfflineChatRefs.current.openModal()} className="text-md hover:cursor-pointer phone:text-xs font-medium text-neutral-40 phone:leading-[20.4px]">Live Chat (Offline)</p>
                            </div>
                            <Link to="/support/knowledge-base">
                                <div className="flex items-center space-x-4 mt-4">
                                    <img src="/icons/support/clipboard.svg" className="phone:w-5 phone:h-5" width={24} height={24} alt="knowlegde base" />
                                    <p className="text-md phone:text-xs font-medium text-neutral-40 phone:leading-[20.4px]">Knowledge Base</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>
        </Fragment>
    );
};

export default Hero;